import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { toast } from 'sonner';
import { toNoonUTC } from '@/shared/lib/utils';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { shortFormIntakeApi } from '@client/api/waterlily';

import type { QuickPlanFormValues, QuickPlanPersonValues } from '@/features/client-quick-plan';

interface UseShortFormIntakeOptions {
  advisorId: string;
  onSuccess?: (data: ShortFormIntakeResponse) => void;
}

function mapFormToRequest(
  formValues: QuickPlanPersonValues,
  advisorId: string,
): ShortFormIntakeRequest {
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
  };

  const smokerMap: Record<string, string> = {
    never: 'No',
    former: 'Yes',
    current: 'Yes',
  };

  const intakeFormUrl = getIntroIntakeFormApiEndPoint(advisorId, false);

  return {
    clientFirstName: formValues.firstName,
    clientLastName: formValues.lastName,
    clientBirthDate: toNoonUTC(formValues.birthDate),
    clientGenderAtBirth: genderMap[formValues.sex] ?? 'Male',
    clientSmokerEver: smokerMap[formValues.smokingStatus] ?? 'No',
    clientMaritalStatus: formValues.maritalStatus,
    clientZipCode: formValues.zipCode,
    intakeFormUrl,
  };
}

export function useShortFormIntake({ advisorId, onSuccess }: UseShortFormIntakeOptions) {
  const navigate = useNavigate();
  let lastCoupleLinked = true;

  const mutation = useMutation({
    mutationFn: async (formValues: QuickPlanFormValues): Promise<ShortFormIntakeResponse> => {
      const primaryRequest = mapFormToRequest(formValues, advisorId);

      if (formValues.hasPartner && formValues.partner) {
        const partnerRequest = mapFormToRequest(formValues.partner, advisorId);
        const coupleResponse = await shortFormIntakeApi.createCouple({
          primary: primaryRequest,
          partner: partnerRequest,
        });
        lastCoupleLinked = coupleResponse.linked;
        return coupleResponse.primary;
      }

      return shortFormIntakeApi.create(primaryRequest);
    },
    onSuccess: (data, variables) => {
      if (variables.hasPartner) {
        if (lastCoupleLinked) {
          toast.success('Quick plan created for both clients');
        } else {
          toast.warning(
            'Quick plans created for both clients, but linking failed. Please link them manually from your dashboard.',
          );
        }
      } else {
        toast.success('Quick plan created successfully');
      }

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/advisors/${advisorId}/clients`);
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create quick plan. Please try again.';
      toast.error(errorMessage);
    },
  });

  return {
    submitQuickPlan: mutation.mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
